import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, someTarget, someTween } from '../skillUsage';

export const strikethroughReplaceSkill: CodeoSkill = {
    name: SKILL_NAMES.strikethroughReplace,
    kind: 'codeo',
    description:
        'Strike a word out and replace it with another in place — the correction move, for ' +
        '"not X, Y", a price drop, a reframing, or overturning an assumption the viewer arrived with.',
    checkLooksFor:
        'one element, tweened by a quoted selector, that is scaled on scaleX and faded to opacity: 0 — the strike rule — and a tween bringing the replacement to opacity: 1',
    documentUsesSkill: check(
        (script) =>
            someTarget(script, /scaleX/, /opacity:\s*0\b/) && someTween(script, /opacity:\s*1\b/),
    ),
    reviewNote:
        'A rule drawn across the old word, the old and new word both readable for a moment in ' +
        'the same slot, and the new word replacing the old in place are this technique working — ' +
        'not text obscured by a shape, not text overlapping, and not a slideshow cut.',
    instructions: `# Strikethrough Replace

A word in a line gets crossed out and a different word takes its place. Both states are visible at the moment of the swap, which is the entire point: the viewer sees what was rejected, so the replacement carries an argument rather than just a fact.

Reach for it whenever the brief has a "not this, that" shape — old price to new price, a term being corrected, an assumption being overturned.

## Markup

The two words share a slot so the line doesn't reflow. Stack them absolutely inside an inline-block sized to the wider of the two.

\`\`\`html
<div id="claim" style="position: absolute; left: 200px; top: 470px; font-family: var(--l5-font-0), sans-serif; font-size: 96px; font-weight: 700; color: var(--l5-color-0, #101820);">
  Setup takes
  <span id="swap-slot" style="position: relative; display: inline-block; width: 380px; height: 110px; vertical-align: top;">
    <span id="swap-old" style="position: absolute; left: 0; top: 0; white-space: nowrap; color: var(--l5-color-1, #5b6472);">three weeks</span>
    <span id="swap-new" style="position: absolute; left: 0; top: 0; white-space: nowrap; color: var(--l5-color-2, #38bdf8);">two days</span>
  </span>
  <span id="swap-rule" data-l5-allow-overlap style="position: absolute; left: 0; top: 0; width: 380px; height: 8px; background: var(--l5-color-2, #38bdf8); transform-origin: left center;"></span>
</div>
\`\`\`

Position \`#swap-rule\` over the old word's centre line with \`left\`/\`top\` values measured from the slot, not with a transform — it is \`scaleX\`-tweened. It carries \`data-l5-allow-overlap\` because a rule laid across letterforms is normally a defect, and here it is the whole point.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const STRIKE = 1.1;

gsap.set('#swap-rule', { scaleX: 0 });
gsap.set('#swap-new', { y: 26, opacity: 0 });

tl.to('#swap-rule', { scaleX: 1, duration: 0.34, ease: 'power2.inOut' }, STRIKE);
tl.to('#swap-old', { opacity: 0.32, duration: 0.3, ease: 'power2.out' }, STRIKE + 0.2);
tl.to('#swap-old', { y: -18, opacity: 0, duration: 0.34, ease: 'power2.in' }, STRIKE + 0.42);
tl.to('#swap-rule', { opacity: 0, duration: 0.3, ease: 'power2.in' }, STRIKE + 0.46);
tl.to('#swap-new', { y: 0, opacity: 1, duration: 0.42, ease: 'power3.out' }, STRIKE + 0.5);

window.__timelines = { main: tl };
\`\`\`

The old word dims *before* it leaves, so the strike reads as the cause of its departure rather than as decoration drawn over it.

## Adapt

- **Strike speed**: 0.3–0.4s. Slower reads as underlining; faster is missed entirely.
- **Hand-drawn strike**: swap the rule for an SVG path with a slight waver and draw it with a dash offset — see \`${SKILL_NAMES.svgDrawOn}\`. Reads as annotation rather than as a system correction.
- **Price drop**: keep the struck price visible at low opacity instead of removing it, so the delta stays legible.
- **Replace upward**: the new word rising from below reads as an improvement; dropping from above reads as a correction.
- **Whole-line replace**: the same choreography works on a full line, but the strike then takes long enough that the timing needs stretching to about 0.6s.
- **On the word**: with narration, \`STRIKE\` is the onset of the rejected word and the replacement lands on the onset of the new one.

## Pitfalls

- Size the slot to the *wider* of the two words. If the replacement is longer it will overflow, and if the slot auto-sizes the line reflows at the swap.
- The rule is \`scaleX\`-tweened from a left origin and must not carry a CSS \`transform\`.
- Don't strike and replace in the same instant. The gap between them — where both are readable — is where the argument happens.
- Both words must be genuinely short. This is a word-level move; a struck clause is a different, slower technique.
- Once per graphic. Two corrections in one scene means neither is the point.
- The strike *is* the "not", so strike the rejected claim stated plainly: "a cost", never "not a cost" — a struck negative reads as its opposite. Drop the "not" from the struck words even where your plan's copy carries it, and don't also say "not three weeks — two days" in on-screen copy elsewhere.
- \`gsap.set\` establishes the from-states at build time, so no tween needs \`immediateRender: false\`.`,
};
